import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, Video, Phone, MapPin, Link2, ExternalLink, Search, X, Filter, Ban,
         CheckCircle2, XCircle, RefreshCw, Users, CalendarClock, User, Calendar as CalIcon } from "lucide-react";
import { StatusBadge, StatusTabs, formatDateTime } from "@/lib/ui";
import { Link } from "react-router-dom";

const DRAFT_KEY = "cd-new-booking-draft";

const STATUS_TABS = [
  { key: "upcoming", label: "Upcoming" },
  { key: "today", label: "Today" },
  { key: "pending", label: "Pending" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];
const LOC_ICON = { google_meet: Video, teams: Video, zoom_manual: Video, phone: Phone, in_person: MapPin, custom_link: Link2 };
const LOC_LABEL = { google_meet: "Google Meet", teams: "Microsoft Teams", zoom_manual: "Zoom", phone: "Phone", in_person: "In Person", custom_link: "Custom link" };

function fmtTime(iso) { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
function fmtDay(iso) { return new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }); }

export default function BookingsDashboard() {
  const { currentOrgId } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ today: 0, upcoming: 0, pending: 0, week: 0, completed: 0, cancelled: 0 });
  const initTab = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tab")) || "upcoming";
  const [tab, setTab] = useState(initTab);
  const [q, setQ] = useState("");
  const [locFilter, setLocFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);   // booking + activity
  const [createOpen, setCreateOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);

  useEffect(() => { if (currentOrgId) load(); }, [currentOrgId, tab]);

  // Sync current status tab to URL so Back-nav from a booking detail restores it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = new URLSearchParams(window.location.search);
    if (tab && tab !== "upcoming") p.set("tab", tab); else p.delete("tab");
    const q = p.toString();
    window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
  }, [tab]);

  // Deep-link: open ?id=xxx booking detail drawer when arriving from another page.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (id && !detail) {
      openDetail(id);
      params.delete("id");
      const q = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
    }
    /* eslint-disable-next-line */
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ organization_id: currentOrgId });
      const now = new Date();
      if (tab === "upcoming") { params.set("status", "confirmed"); params.set("from_date", now.toISOString()); }
      else if (tab === "today") {
        params.set("status", "confirmed");
        const s = new Date(now); s.setHours(0,0,0,0);
        const e = new Date(s); e.setDate(e.getDate() + 1);
        params.set("from_date", s.toISOString()); params.set("to_date", e.toISOString());
      }
      else if (tab === "pending") params.set("status", "pending");
      else if (tab === "completed") params.set("status", "completed");
      else if (tab === "cancelled") params.set("status", "cancelled");
      if (q) params.set("q", q);
      const r = await apiFetch(`/scheduling/bookings?${params}`);
      setRows(r.bookings || []);
      setCounts(r.counts || {});
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    if (locFilter === "all") return rows;
    return rows.filter((r) => r.location_type === locFilter);
  }, [rows, locFilter]);

  const canJoinNow = (b) => {
    if (!b.meeting_url) return false;
    const t = new Date(b.starts_at).getTime();
    return Date.now() >= t - 15 * 60 * 1000 && Date.now() <= new Date(b.ends_at).getTime();
  };

  const openDetail = async (id) => {
    try { const r = await apiFetch(`/scheduling/bookings/${id}`); setDetail(r); }
    catch (e) { toast.error(e.message); }
  };

  const doAction = async (path, body) => {
    try {
      await apiFetch(path, { method: "POST", body: body ? JSON.stringify(body) : undefined });
      toast.success("Done");
      if (detail) await openDetail(detail.booking.id);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4" data-testid="bookings-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#57534E]">Manage client appointments and scheduling.</p>
        <Button data-testid="booking-new" onClick={() => setCreateOpen(true)}
                className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
          <Plus size={14} className="mr-1.5" /> New Booking
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: "today", label: "Today", tab: "today" },
          { k: "upcoming", label: "Upcoming", tab: "upcoming" },
          { k: "pending", label: "Pending", tab: "pending" },
          { k: "week", label: "This Week", tab: "upcoming" },
        ].map((c) => (
          <button key={c.k} onClick={() => setTab(c.tab)} data-testid={`booking-card-${c.k}`}
            className="bg-white rounded-2xl border border-[#E8E2D9] p-3 text-left hover:border-[#E8712C]/40 transition-colors">
            <div className="text-xs text-[#57534E]">{c.label}</div>
            <div className="text-2xl font-display font-bold mt-1">{counts[c.k] ?? 0}</div>
          </button>
        ))}
      </div>

      {/* Status tabs — shared component used by Invoices/Contracts/SR */}
      <StatusTabs
        tabs={STATUS_TABS.map((t) => ({ id: t.key, label: t.label }))}
        value={tab}
        onChange={setTab}
        counts={counts}
        testidPrefix="booking-tab"
      />

      {/* Search + filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
          <Input placeholder="Search by client name, email…" value={q}
                 onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 className="pl-9" data-testid="booking-search" />
        </div>
        <Select value={locFilter} onValueChange={setLocFilter}>
          <SelectTrigger className="w-40" data-testid="booking-filter-loc"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            {Object.entries(LOC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} className="rounded-full h-9" data-testid="booking-refresh">
          <RefreshCw size={13} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Rows */}
      {loading ? <p className="text-sm text-[#A8A29E] py-6 text-center">Loading…</p> : (
        <div className="space-y-2">
          {filtered.length === 0 && (
            <div className="bg-white border border-dashed border-[#E8E2D9] rounded-2xl p-10 text-center text-sm text-[#A8A29E]">
              No bookings.
            </div>
          )}
          {filtered.map((b) => {
            const Icon = LOC_ICON[b.location_type] || Video;
            const label = LOC_LABEL[b.location_type] || "";
            const apt = b.appointment_type_snapshot || {};
            return (
              <div key={b.id} className="bg-white border border-[#E8E2D9] rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap"
                   data-testid={`booking-row-${b.id}`}>
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="text-center min-w-[62px]">
                    <div className="text-xs text-[#57534E]">{fmtDay(b.starts_at)}</div>
                    <div className="font-display font-bold text-lg leading-tight">{fmtTime(b.starts_at)}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{b.client_first_name} {b.client_last_name}</div>
                    <div className="text-xs text-[#57534E] truncate">
                      {apt.name || "Appointment"} · {b.duration_minutes} min
                    </div>
                    <div className="text-xs text-[#A8A29E] flex items-center gap-1.5 mt-0.5">
                      <Icon size={11} /> {label}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {tab === "pending" && (
                    <>
                      <Button size="sm" onClick={() => doAction(`/scheduling/bookings/${b.id}/approve`)}
                              className="rounded-full h-8 bg-[#2C3A29] hover:bg-[#3E4C38] text-white"
                              data-testid={`booking-approve-${b.id}`}><CheckCircle2 size={12} className="mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => doAction(`/scheduling/bookings/${b.id}/decline`)}
                              className="rounded-full h-8" data-testid={`booking-decline-${b.id}`}><XCircle size={12} className="mr-1" /> Decline</Button>
                    </>
                  )}
                  {canJoinNow(b) && (
                    <a href={b.meeting_url} target="_blank" rel="noreferrer"
                       data-testid={`booking-join-${b.id}`}
                       className="inline-flex items-center gap-1 rounded-full h-8 px-3 text-xs font-medium bg-[#E8712C] text-white hover:bg-[#C55A1F]">
                      Join <ExternalLink size={10} />
                    </a>
                  )}
                  <StatusBadge value={b.status} />
                  <Button size="sm" variant="outline" onClick={() => openDetail(b.id)}
                          className="rounded-full h-8" data-testid={`booking-view-${b.id}`}>View</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      {detail && (
        <BookingDetail
          data={detail}
          onClose={() => setDetail(null)}
          onAction={doAction}
          onReschedule={(b) => { setRescheduleTarget(b); setDetail(null); }}
        />
      )}

      {createOpen && (
        <NewBookingDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}
        />
      )}
      {rescheduleTarget && (
        <RescheduleDialog booking={rescheduleTarget}
                          onClose={() => setRescheduleTarget(null)}
                          onDone={() => { setRescheduleTarget(null); load(); }} />
      )}
    </div>
  );
}

function BookingDetail({ data, onClose, onAction, onReschedule }) {
  const b = data.booking;
  const apt = b.appointment_type_snapshot || {};
  const Icon = LOC_ICON[b.location_type] || Video;
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{apt.name || "Booking"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><b className="text-[#57534E]">When:</b> {formatDateTime(b.starts_at)} ({b.timezone || "UTC"})</div>
            <div><b className="text-[#57534E]">Duration:</b> {b.duration_minutes} min</div>
            <div><b className="text-[#57534E]">Client:</b>
              {b.client_id ? (
                <Link to={`/app/clients/${b.client_id}`} className="text-[#E8712C] ml-1">{b.client_first_name} {b.client_last_name}</Link>
              ) : <span className="ml-1">{b.client_first_name} {b.client_last_name}</span>}
            </div>
            <div><b className="text-[#57534E]">Email:</b> {b.client_email || "—"}</div>
            <div><b className="text-[#57534E]">Location:</b> <span className="inline-flex items-center gap-1"><Icon size={12} /> {LOC_LABEL[b.location_type]}</span></div>
            <div><b className="text-[#57534E]">Status:</b> <StatusBadge value={b.status} /></div>
          </div>
          {b.meeting_url && (
            <div className="p-3 rounded-xl bg-[#FDE9D8] border border-[#F5C9A6]">
              <div className="text-xs text-[#B85C1F] uppercase tracking-wider mb-1">Meeting link</div>
              <a href={b.meeting_url} target="_blank" rel="noreferrer" className="text-sm font-medium text-[#1C1917] break-all">{b.meeting_url}</a>
            </div>
          )}
          {b.client_notes && <div className="p-3 rounded-xl bg-[#F5F5F4] text-sm"><b className="text-[#57534E]">Client notes:</b> {b.client_notes}</div>}
          {b.client_answers && Object.keys(b.client_answers).length > 0 && (() => {
            const qs = (b.appointment_type_snapshot?.custom_questions) || [];
            const qByKey = Object.fromEntries(qs.map((q) => [q.key || q.label, q]));
            return (
              <div className="p-3 rounded-xl bg-[#F5F5F4] text-sm space-y-1.5" data-testid="detail-answers">
                <b className="text-[#57534E]">Client answers</b>
                {Object.entries(b.client_answers).map(([k, v]) => (
                  <div key={k} className="text-xs">
                    <div className="text-[#57534E] font-medium">{qByKey[k]?.label || k.replace(/_/g, " ")}</div>
                    <div className="text-[#1C1917]">{Array.isArray(v) ? v.join(", ") : String(v)}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          <div className="p-3 rounded-xl border border-[#E8E2D9]">
            <div className="text-xs text-[#57534E] uppercase tracking-wider mb-1">Calendar sync</div>
            <div className="text-xs">Google: {b.google_event_id ? "✓ Synced" : "—"} · Outlook: {b.outlook_event_id ? "✓ Synced" : "—"}</div>
          </div>
          <div className="border-t border-[#E8E2D9] pt-3">
            <div className="text-xs font-semibold text-[#57534E] uppercase tracking-widest mb-2">Activity</div>
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {(data.activity || []).map((a) => (
                <div key={a.id} className="text-xs text-[#57534E] flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E8712C] mt-1.5 flex-shrink-0" />
                  <span><b className="text-[#1C1917] capitalize">{a.action.replace(/_/g, " ")}</b> · {new Date(a.created_at).toLocaleString()}
                    {a.actor_label && <span> · by {a.actor_label}</span>}
                  </span>
                </div>
              ))}
              {(data.activity || []).length === 0 && <p className="text-xs text-[#A8A29E]">No activity.</p>}
            </div>
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {b.status === "confirmed" && b.meeting_url && (
            <a href={b.meeting_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-[#E8712C] text-white text-sm hover:bg-[#C55A1F]"
               data-testid="detail-join">Join meeting <ExternalLink size={12} /></a>
          )}
          {["confirmed", "pending"].includes(b.status) && (
            <Button variant="outline" className="rounded-full" onClick={() => onReschedule(b)}
                    data-testid="detail-reschedule"><CalendarClock size={12} className="mr-1" /> Reschedule</Button>
          )}
          {["confirmed", "pending"].includes(b.status) && (
            <Button variant="outline" className="rounded-full" data-testid="detail-cancel"
                    onClick={() => { const reason = prompt("Cancellation reason?") || ""; onAction(`/scheduling/bookings/${b.id}/cancel`, { reason }); }}>
              <Ban size={12} className="mr-1" /> Cancel
            </Button>
          )}
          {b.status === "confirmed" && (
            <>
              <Button variant="outline" className="rounded-full" onClick={() => onAction(`/scheduling/bookings/${b.id}/complete`)}
                      data-testid="detail-complete">Mark Completed</Button>
              <Button variant="outline" className="rounded-full" onClick={() => onAction(`/scheduling/bookings/${b.id}/no-show`)}
                      data-testid="detail-noshow">No Show</Button>
            </>
          )}
          <Button onClick={onClose} className="rounded-full bg-[#2C3A29] hover:bg-[#3E4C38] text-white">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function loadDraft() {
  try {
    const raw = typeof window !== "undefined" && window.sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveDraft(d) {
  try { if (typeof window !== "undefined") window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch {}
}
function clearDraft() {
  try { if (typeof window !== "undefined") window.sessionStorage.removeItem(DRAFT_KEY); } catch {}
}

function NewBookingDialog({ onClose, onCreated }) {
  const { currentOrgId } = useAuth();
  const draft = loadDraft() || {};
  const [types, setTypes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [aptId, setAptId] = useState(draft.aptId || "");
  const [hostId, setHostId] = useState(draft.hostId || "");
  const [clientId, setClientId] = useState(draft.clientId || "");
  const [manualClient, setManualClient] = useState(draft.manualClient || { first_name: "", last_name: "", email: "", phone: "" });
  const [date, setDate] = useState(draft.date || (() => new Date().toISOString().slice(0,10)));
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(draft.selected || null);
  const [notes, setNotes] = useState(draft.notes || "");
  const [notify, setNotify] = useState(draft.notify !== undefined ? draft.notify : true);
  const [force, setForce] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Persist draft on every change so an accidental session refresh, tab close,
  // or transient 401 during submit never destroys the entered client + time.
  useEffect(() => {
    saveDraft({ aptId, hostId, clientId, manualClient, date, selected, notes, notify });
  }, [aptId, hostId, clientId, manualClient, date, selected, notes, notify]);

  useEffect(() => {
    if (!currentOrgId) return;
    Promise.all([
      apiFetch(`/scheduling/appointment-types?organization_id=${currentOrgId}`),
      apiFetch(`/scheduling/staff?organization_id=${currentOrgId}`),
    ]).then(([t, s]) => { setTypes((t.appointment_types || []).filter((x) => x.active)); setStaff(s.staff || []); })
      .catch(() => {}); // never bubble background errors past this dialog
  }, [currentOrgId]);

  // Fetch REAL availability slots from backend — respects weekly + overrides +
  // buffers + existing bookings + Google/Outlook busy. Never returns a fake
  // 06:00–22:00 dropdown any more.
  useEffect(() => {
    if (!aptId || !date || !currentOrgId) { setSlots([]); return; }
    setLoadingSlots(true);
    const qs = new URLSearchParams({
      organization_id: currentOrgId,
      appointment_type_id: aptId,
      from_date: date,
    });
    if (hostId) qs.set("host_user_id", hostId);
    apiFetch(`/scheduling/va-slots?${qs}`)
      .then((r) => {
        const rawSlots = (r.days && r.days[date]) || [];
        // Defensive dedupe by starts_at — backend also merges overlapping
        // weekly ranges now, but never rely on it for React keys.
        const seen = new Set();
        const daySlots = rawSlots.filter((s) => {
          if (seen.has(s.starts_at)) return false;
          seen.add(s.starts_at);
          return true;
        });
        setSlots(daySlots);
        // If previously-selected time is no longer in the fresh slot list, clear it.
        if (selected && !daySlots.find((s) => s.starts_at === selected.starts_at)) {
          setSelected(null);
        }
      })
      .catch((e) => {
        // Calendar OAuth / RLS 401s used to kick the VA to /login. Now the
        // request just returns [] and the UI shows a friendly empty state.
        setSlots([]);
        toast.error(`Couldn't load times: ${e.message}`);
      })
      .finally(() => setLoadingSlots(false));
    // eslint-disable-next-line
  }, [aptId, date, hostId, currentOrgId]);

  const submit = async () => {
    if (!aptId || !selected) return toast.error("Pick appointment type and time");
    if (!clientId && (!manualClient.first_name || !manualClient.last_name)) return toast.error("Client first and last name required");
    setSaving(true);
    try {
      await apiFetch("/scheduling/bookings", {
        method: "POST",
        body: JSON.stringify({
          organization_id: currentOrgId,
          appointment_type_id: aptId,
          host_user_id: hostId || undefined,
          client_id: clientId || undefined,
          client_first_name: clientId ? undefined : manualClient.first_name,
          client_last_name: clientId ? undefined : manualClient.last_name,
          client_email: clientId ? undefined : manualClient.email,
          client_phone: clientId ? undefined : manualClient.phone,
          starts_at: selected.starts_at,
          notify, client_notes: notes, force,
        }),
      });
      toast.success("Booking created");
      clearDraft();
      onCreated();
    } catch (e) {
      // Preserve everything the VA typed. Show a friendly explanation for the
      // most common recoverable error (slot taken between preview and submit).
      const msg = String(e.message || "");
      if (msg.includes("slot_not_available") || msg.includes("slot_taken_race")) {
        toast.error("That time is no longer available. Pick a different slot.");
        setSelected(null);
      } else if (msg.toLowerCase().includes("calendar") || msg.toLowerCase().includes("oauth")) {
        toast.error("Calendar sync is not available right now — your booking was not saved. Try again or use Book anyway.");
        setForce(true);
      } else if (msg && msg.length < 120) {
        toast.error(msg);
      } else {
        toast.error("Something went wrong — your booking was not saved. Please try again.");
      }
    } finally { setSaving(false); }
  };

  const closeDialog = () => { onClose(); /* keep sessionStorage draft so a reopened dialog restores fields */ };

  const prettyDate = date ? new Date(date + "T00:00:00").toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Pick a date";

  return (
    <Dialog open={true} onOpenChange={(v) => !v && closeDialog()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">New Booking</DialogTitle>
          <DialogDescription className="text-xs text-[#57534E]">
            Create an appointment on behalf of a client. Times shown are your true available slots.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Appointment type</Label>
              <Select value={aptId} onValueChange={setAptId}>
                <SelectTrigger className="mt-1.5" data-testid="new-apt-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Staff (host)</Label>
              <Select value={hostId} onValueChange={setHostId}>
                <SelectTrigger className="mt-1.5" data-testid="new-host"><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="border border-[#E8E2D9] rounded-2xl p-3 space-y-2">
            <Label className="text-xs uppercase tracking-widest text-[#57534E]">Client</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="First name" value={manualClient.first_name}
                     onChange={(e) => setManualClient({...manualClient, first_name: e.target.value})}
                     data-testid="new-client-first" />
              <Input placeholder="Last name" value={manualClient.last_name}
                     onChange={(e) => setManualClient({...manualClient, last_name: e.target.value})}
                     data-testid="new-client-last" />
              <Input placeholder="Email" type="email" value={manualClient.email}
                     onChange={(e) => setManualClient({...manualClient, email: e.target.value})}
                     data-testid="new-client-email" />
              <Input placeholder="Phone" value={manualClient.phone}
                     onChange={(e) => setManualClient({...manualClient, phone: e.target.value})}
                     data-testid="new-client-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <button data-testid="new-date"
                          className="mt-1.5 w-full inline-flex items-center gap-2 h-10 px-3 rounded-md border border-[#E8E2D9] bg-white text-sm hover:border-[#E8712C] text-left">
                    <CalIcon size={13} strokeWidth={1.75} className="text-[#57534E]" />
                    <span className="flex-1 truncate">{prettyDate}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto bg-white">
                  <Calendar mode="single" selected={date ? new Date(date + "T00:00:00") : undefined}
                            onSelect={(d) => {
                              if (!d) return;
                              const iso = d.toISOString().slice(0, 10);
                              setDate(iso); setSelected(null); setDatePickerOpen(false);
                            }}
                            disabled={(d) => { const t = new Date(); t.setHours(0,0,0,0); return d < t; }} />
                </PopoverContent>
              </Popover>
            </div>
            <div><Label>Time</Label>
              <Select value={selected?.starts_at || ""} onValueChange={(v) => setSelected(slots.find((s) => s.starts_at === v))}>
                <SelectTrigger className="mt-1.5" data-testid="new-time"><SelectValue placeholder={loadingSlots ? "Loading…" : (slots.length ? "Select time…" : "No times available")} /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {loadingSlots ? <div className="p-3 text-xs text-[#A8A29E]">Loading available times…</div> :
                    slots.length === 0 ? <div className="p-3 text-xs text-[#A8A29E]">No available times for this day. Change the date or check your availability settings.</div> :
                    slots.map((s) => <SelectItem key={s.starts_at} value={s.starts_at}>{fmtTime(s.starts_at)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="mt-1.5" data-testid="new-notes" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
                   data-testid="new-notify" />
            Send confirmation to client
          </label>
          {force && (
            <label className="flex items-center gap-2 text-sm text-[#B02222]">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                     data-testid="new-force" />
              Book anyway (override calendar conflict)
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="new-booking-cancel" onClick={() => { clearDraft(); closeDialog(); }} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-booking-submit"
                  className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
            {saving ? "Creating…" : "Create Booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RescheduleDialog({ booking, onClose, onDone }) {
  const [newStart, setNewStart] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!newStart) return toast.error("Pick a time");
    setSaving(true);
    try {
      await apiFetch(`/scheduling/bookings/${booking.id}/reschedule`, {
        method: "POST",
        body: JSON.stringify({ starts_at: new Date(newStart).toISOString(), notify: true }),
      });
      toast.success("Rescheduled"); onDone();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Reschedule</DialogTitle></DialogHeader>
        <div className="py-2 space-y-2">
          <div className="text-sm text-[#57534E]">Current: {formatDateTime(booking.starts_at)}</div>
          <Label>New date &amp; time</Label>
          <Input type="datetime-local" value={newStart} onChange={(e) => setNewStart(e.target.value)}
                 data-testid="reschedule-time" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="reschedule-submit"
                  className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
            {saving ? "Saving…" : "Reschedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
